<template>
  <div class="animated fadeIn">
    <Loader :isVisible="loading" />

    <MarketingFilters
      :default="filters"
      :values="values"
      :countClient="clients.length"
      @formFilters="handleFormFilters"
      @updateFilter="handleFilters"
    />

    <MarketingStats v-if="cols && !loading" :cols="cols" />
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import moment from "moment";

import MarketingFilters from "@/components/stats/MarketingFilters";
import MarketingStats from "@/components/stats/MarketingStats";
import Loader from "@/components/utils/Loader";

export default {
  components: {
    MarketingFilters,
    MarketingStats,
    Loader,
  },

  data() {
    return {
      loading: false,

      cols: undefined,

      clients: [],
      expenses: [],

      formFilters: {
        begin: moment().startOf("M").format(),
        end: moment().endOf("M").format(),
        commercial: null,
        clientType: [],
      },

      filters: {},

      values: {
        utm_sources: [],
        utm_mediums: [],
        utm_terms: [],
        utm_contents: [],
        utm_campaigns: [],
        sourceUrls: [],
        referers: [],
        expenses: [],
        status: [
          {
            label: "Découverte",
            value: "decouverte",
          },
          {
            label: "Conversion",
            value: "pack-conversion",
          },
          {
            label: "OneShot/Event",
            value: "one-shot",
          },
          {
            label: "Fidélisation",
            value: "pack-fidelisation",
          },
          {
            label: "Pack Autres",
            value: "autres",
          },
        ],
      },

      newMarketingSystemDeployDate: moment("2025-02-12").toDate(),
    };
  },

  computed: {
    ...mapGetters({
      lostReasons: "getLostReasons",
    }),

    useFormerMarketingSystem() {
      return moment(this.formFilters.begin).isBefore(
        this.newMarketingSystemDeployDate,
      );
    },

    clientsFromFormerMarketingSystem() {
      return this.clients.filter(
        (c) =>
          c.prospect?.lastFormFilledAt &&
          moment(c.prospect.lastFormFilledAt).isBefore(
            this.newMarketingSystemDeployDate,
          ),
      );
    },
  },

  created() {
    this.loadData();
  },

  methods: {
    fetchMarketingFilters() {
      const sources = this.expenses
        .filter((e) => e.source)
        .map((e) => e.source);

      if (this.useFormerMarketingSystem) {
        sources.push(
          ...this.clientsFromFormerMarketingSystem
            .filter((c) => c.marketing?.utm_source)
            .map((c) => c.marketing.utm_source),
        );
      }

      sources.push(
        ...this.clients
          .map((c) =>
            c.marketings
              .filter((cm) => cm.marketing?.utm_source)
              .map((cm) => cm.marketing.utm_source),
          )
          .flat(),
      );

      const sourcesValues = sources
        .filter((item, index) => {
          return sources.indexOf(item) == index;
        })
        .map((s) => ({ value: s, label: s }));

      this.values.utm_sources = [{ type: "Tous", values: sourcesValues }];

      const campaigns = this.expenses
        .filter((e) => e.campaign)
        .map((e) => e.campaign);

      if (this.useFormerMarketingSystem) {
        campaigns.push(
          ...this.clientsFromFormerMarketingSystem
            .filter((c) => c.marketing?.utm_campaign)
            .map((c) => c.marketing.utm_campaign),
        );
      }

      campaigns.push(
        ...this.clients
          .map((c) =>
            c.marketings
              .filter((cm) => cm.marketing?.utm_campaign)
              .map((cm) => cm.marketing.utm_campaign),
          )
          .flat(),
      );

      const campaignsValues = campaigns
        .filter((item, index) => {
          return campaigns.indexOf(item) == index;
        })
        .map((s) => ({ value: s, label: s }));

      this.values.utm_campaigns = [{ type: "Tous", values: campaignsValues }];

      const mediums = this.expenses
        .filter((e) => e.medium)
        .map((e) => e.medium);

      if (this.useFormerMarketingSystem) {
        mediums.push(
          ...this.clientsFromFormerMarketingSystem
            .filter((c) => c.marketing?.utm_medium)
            .map((c) => c.marketing.utm_medium),
        );
      }

      mediums.push(
        ...this.clients
          .map((c) =>
            c.marketings
              .filter((cm) => cm.marketing?.utm_medium)
              .map((cm) => cm.marketing.utm_medium),
          )
          .flat(),
      );

      const mediumsValues = mediums
        .filter((item, index) => {
          return mediums.indexOf(item) == index;
        })
        .map((s) => ({ value: s, label: s }));

      this.values.utm_mediums = [{ type: "Tous", values: mediumsValues }];

      const terms = this.expenses.filter((e) => e.term).map((e) => e.term);

      if (this.useFormerMarketingSystem) {
        terms.push(
          ...this.clientsFromFormerMarketingSystem
            .filter((c) => c.marketing?.utm_term)
            .map((c) => c.marketing.utm_term),
        );
      }

      terms.push(
        ...this.clients
          .map((c) =>
            c.marketings
              .filter((cm) => cm.marketing?.utm_term)
              .map((cm) => cm.marketing.utm_term),
          )
          .flat(),
      );

      const termsValues = terms
        .filter((item, index) => {
          return terms.indexOf(item) == index;
        })
        .map((s) => ({ value: s, label: s }));

      this.values.utm_terms = [{ type: "Tous", values: termsValues }];

      const contents = this.expenses
        .filter((e) => e.content)
        .map((e) => e.content);

      if (this.useFormerMarketingSystem) {
        contents.push(
          ...this.clientsFromFormerMarketingSystem
            .filter((c) => c.marketing?.utm_content)
            .map((c) => c.marketing.utm_content),
        );
      }

      contents.push(
        ...this.clients
          .map((c) =>
            c.marketings
              .filter((cm) => cm.marketing?.utm_content)
              .map((cm) => cm.marketing.utm_content),
          )
          .flat(),
      );

      const contentsValue = contents
        .filter((item, index) => {
          return contents.indexOf(item) == index;
        })
        .map((s) => ({ value: s, label: s }));

      this.values.utm_contents = [{ type: "Tous", values: contentsValue }];

      const sourceUrls = this.expenses
        .filter((e) => e.sourceUrl)
        .map((e) => e.sourceUrl);

      if (this.useFormerMarketingSystem) {
        sourceUrls.push(
          ...this.clientsFromFormerMarketingSystem
            .filter((c) => c.marketing?.sourceUrl)
            .map((c) => c.marketing.sourceUrl),
        );
      }

      sourceUrls.push(
        ...this.clients
          .map((c) =>
            c.marketings
              .filter((cm) => cm.marketing?.sourceUrl)
              .map((cm) => cm.marketing.sourceUrl),
          )
          .flat(),
      );

      const sourcesUrlsValues = sourceUrls
        .filter((item, index) => {
          return sourceUrls.indexOf(item) == index;
        })
        .map((s) => ({ value: s, label: s }));

      this.values.sourceUrls = [{ type: "Tous", values: sourcesUrlsValues }];

      const referers = this.expenses
        .filter((e) => e.referer)
        .map((e) => e.referer);

      if (this.useFormerMarketingSystem) {
        referers.push(
          ...this.clientsFromFormerMarketingSystem
            .filter((c) => c.marketing?.referer)
            .map((c) => c.marketing.referer),
        );
      }

      referers.push(
        ...this.clients
          .map((c) =>
            c.marketings
              .filter((cm) => cm.marketing?.referer)
              .map((cm) => cm.marketing.referer),
          )
          .flat(),
      );

      const referersValues = referers
        .filter((item, index) => {
          return referers.indexOf(item) == index;
        })
        .map((s) => ({ value: s, label: s }));

      this.values.referers = [{ type: "Tous", values: referersValues }];
    },

    async fetchExpenses() {
      try {
        const { begin, end } = this.formFilters;

        const { data } = await this.$api.get("/expenses", {
          params: { begin, end },
        });

        this.expenses = data.data;

        this.values.expenses = this.expenses.map((v) => ({
          label: `${v.type} : ${v.name}`,
          value: v,
        }));
      } catch (e) {
        this.$errorToast("Impossible de charger les dépenses");
      }
    },

    async fetchClients() {
      try {
        const { begin, end, commercial, clientType } = this.formFilters;

        const { data } = await this.$api.get("/marketings/clients", {
          params: {
            begin,
            end,
            commercialEmail: commercial?.value,
            clientType,
          },
        });

        this.clients = data;
      } catch (e) {
        this.$errorToast("Impossible de charger les clients");
      }
    },

    async handleFormFilters(formFilters) {
      this.formFilters = formFilters;
      this.filters = {};
      this.cols = undefined;

      await this.loadData();
    },

    handleFilters(filters) {
      this.filters = filters;

      this.cols = {
        utm_sources: this.getStats(filters.sources, "utm_source", "source"),
        utm_campaigns: this.getStats(
          filters.campaigns,
          "utm_campaign",
          "campaign",
        ),
        utm_mediums: this.getStats(filters.mediums, "utm_medium", "medium"),
        utm_terms: this.getStats(filters.terms, "utm_term", "term"),
        utm_contents: this.getStats(filters.contents, "utm_content", "content"),
        referers: this.getStats(filters.referers, "referer", "referer"),
        sourceUrls: this.getStats(filters.sourceUrls, "sourceUrl", "sourceUrl"),
      };
    },

    async loadData() {
      this.loading = true;
      await this.fetchExpenses();
      await this.fetchClients();

      this.fetchMarketingFilters();
      this.loading = false;
    },

    getCaForClient(clients, type) {
      let total = 0;
      let nbClients = 0;

      for (const client of clients) {
        const orderWithInvoice = client.orders
          .filter((o) => o.invoice?.serial && !o.discovery)
          .filter((o) => {
            switch (type) {
              case "acquisition":
                return o.status === "refunded"
                  ? client.orders.some(
                      (order) =>
                        order.id === o.refundedOrder &&
                        order.isAcquisition &&
                        !order.isFidelisation,
                    )
                  : o.isAcquisition && !o.isFidelisation;
              case "fidelisation":
                return o.status === "refunded"
                  ? client.orders.some(
                      (order) =>
                        order.id === o.refundedOrder && !!order.isFidelisation,
                    )
                  : !!o.isFidelisation;
              default:
                return true;
            }
          });

        if (orderWithInvoice.length > 0) {
          nbClients++;

          total += orderWithInvoice.reduce(
            (acc, o) =>
              o.status !== "refunded" ? acc + o.price : acc - o.price,
            0,
          );
        }
      }

      return {
        total: total / 100,
        average: nbClients > 0 ? total / nbClients / 100 : 0,
      };
    },

    createStat(clients, expenses, marketingValue) {
      const { begin, end } = this.formFilters;

      const { total: ca } = this.getCaForClient(clients);
      const { total: caAcquisition, average: caAcquisitionAverage } =
        this.getCaForClient(clients, "acquisition");
      const { total: caFidelisation, average: caFidelisationAverage } =
        this.getCaForClient(clients, "fidelisation");
      const caAverage = ca / clients.length || 0;

      const amount = expenses.reduce((p, n) => p + n.amount, 0);

      const nbClientsProspects = clients.length;

      const nbProspectsConvert = clients.filter((c) => {
        const ordersInPeriod = c.orders.filter((o) =>
          moment(o.createdAt).isBetween(begin, end, undefined, "[]"),
        );

        return ordersInPeriod.some((o) => o.invoice?.pdf && !!o.discovery);
      }).length;

      const nbClients = clients.filter((c) => {
        const ordersInPeriod = c.orders.filter((o) =>
          moment(o.createdAt).isBetween(begin, end, undefined, "[]"),
        );

        const ordersInPeriodAndBefore = c.orders.filter((o) =>
          moment(o.createdAt).isSameOrBefore(end),
        );

        return (
          ordersInPeriod.length > 0 &&
          ordersInPeriodAndBefore.length > 1 &&
          ordersInPeriod.some((o) => o.invoice?.pdf && !o.discovery) &&
          ordersInPeriodAndBefore.some((o) => !!o.discovery)
        );
      }).length;

      const prospectsLosts = clients.filter(
        (c) =>
          c.prospect?.lostAt &&
          moment(c.prospect.lostAt).isBetween(begin, end, undefined, "[]"),
      );

      const nbProspectsLosts = prospectsLosts.length;

      let lostReasons = "";

      const lostReasonsCount = this.lostReasons.map((r) => ({
        ...r,
        count: 0,
        details: r.details
          ? r.details.map((d) => ({ ...d, count: 0 }))
          : undefined,
      }));

      for (const prospect of prospectsLosts) {
        const reason = lostReasonsCount.find(
          (reason) => reason.value === prospect.prospect?.lostReason,
        );

        if (reason?.details) {
          const detail = reason.details.find(
            (detail) => detail.value === prospect.prospect?.detailLostReason,
          );

          if (detail) detail.count++;
        }

        if (reason) reason.count++;
      }

      for (const reasonObj of lostReasonsCount) {
        if (reasonObj.count > 0) {
          let detailsLostReasons = "";

          if (reasonObj.details) {
            for (const detail of reasonObj.details) {
              if (detail.count > 0) {
                detailsLostReasons += `${
                  detailsLostReasons.length > 0 ? ", " : ""
                }${detail.count} ${detail.label}`;
              }
            }
          }

          lostReasons += `${reasonObj.count} ${reasonObj.label} ${
            detailsLostReasons.length > 0 ? `: (${detailsLostReasons})` : ""
          }\n`;
        }
      }

      const conversionRate =
        nbClients > 0 && nbClientsProspects > 0
          ? (nbClients / nbClientsProspects) * 100
          : 0;

      return {
        marketingValue,
        caAcquisition,
        caAcquisitionAverage,
        caFidelisation,
        caFidelisationAverage,
        ca,
        caAverage,
        amount,
        nbClientsProspects,
        nbProspectsConvert,
        nbClients,
        nbProspectsLosts,
        lostReasons,
        conversionRate,
      };
    },

    getStats(filters, marketingAttribute, expenseAttribute) {
      let stats = [];

      if (!filters) {
        return [];
      }

      for (const filter of filters) {
        const clients = [];

        if (this.useFormerMarketingSystem) {
          clients.push(
            ...this.clientsFromFormerMarketingSystem.filter(
              (c) =>
                c.marketing &&
                c.marketing[marketingAttribute] &&
                c.marketing[marketingAttribute] === filter.value,
            ),
          );
        }

        clients.push(
          ...this.clients.filter((c) =>
            c.marketings.some(
              (cm) =>
                cm.marketing &&
                cm.marketing[marketingAttribute] &&
                cm.marketing[marketingAttribute] === filter.value,
            ),
          ),
        );

        const expenses = this.expenses.filter(
          (e) => e[expenseAttribute] && e[expenseAttribute] === filter.value,
        );

        stats.push(this.createStat(clients, expenses, filter.value));
      }

      return stats;
    },
  },
};
</script>
